import React, { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import Landing from "./index/Landing";
import Login from "./Login";
import AdminLogin from "./admin_pages/AdminLogin";

import ProtectedRoute from "./ProtectedRoute";
import DashboardMedecin from "./medecin_pages/DashboardMedecin";
import DashboardCollectivite from "./collectivite_pages/DashboardCollectivite";
import DashboardAdmin from "./admin_pages/DashboardAdmin";
import DashboardUsager from "./usager_pages/DashboardUsager";

import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";

/* =========================
   Styles du modal session
========================= */
const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(10, 20, 40, 0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 99999,
    padding: 16,
  },
  modal: {
    background: "#fff",
    borderRadius: 20,
    padding: "36px 32px 28px",
    maxWidth: 400,
    width: "100%",
    textAlign: "center",
    boxShadow: "0 24px 60px rgba(10, 20, 50, 0.18)",
    animation: "fadeInUp .18s ease",
  },
  iconWrap: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #EAF7F3, #d0f0e8)",
    border: "1.5px solid #b2e8d8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
    fontSize: 26,
  },
  title: {
    fontSize: 20,
    fontWeight: 900,
    color: "#0a2b66",
    marginBottom: 10,
    letterSpacing: "-0.3px",
  },
  text: {
    fontSize: 14,
    color: "#5c6f91",
    lineHeight: 1.55,
    marginBottom: 24,
  },
  btn: {
    display: "inline-block",
    padding: "12px 32px",
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(135deg, #158F77, #27B597)",
    color: "#fff",
    fontWeight: 900,
    fontSize: 15,
    cursor: "pointer",
    letterSpacing: "-0.1px",
    boxShadow: "0 4px 14px rgba(21,143,119,0.30)",
    transition: "filter .15s",
    width: "100%",
  },
};

function App() {
  const [user, setUser] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const fetchPatched = useRef(false);

  /* Persist session (refresh safe) */
  useEffect(() => {
    const raw = localStorage.getItem("formidoc_user");
    if (raw) setUser(JSON.parse(raw));
  }, []);

  /* Intercepteur fetch global : détecte 401 sur les routes authentifiées */
  useEffect(() => {
    if (fetchPatched.current) return;
    fetchPatched.current = true;

    const originalFetch = window.fetch;

    window.fetch = async (url, options = {}) => {
      const res = await originalFetch(url, options);

      if (res.status === 401) {
        const headers = options?.headers || {};
        const hasAuth =
          headers["Authorization"] || headers["authorization"];

        if (hasAuth) {
          // clone la réponse avant de la retourner (on ne peut lire le body qu'une fois)
          window.dispatchEvent(new Event("formidoc:session-expired"));
        }
      }

      return res;
    };

    return () => {
      window.fetch = originalFetch;
      fetchPatched.current = false;
    };
  }, []);

  /* Écoute l'événement session expirée */
  useEffect(() => {
    const handler = () => setSessionExpired(true);
    window.addEventListener("formidoc:session-expired", handler);
    return () => window.removeEventListener("formidoc:session-expired", handler);
  }, []);

  const handleLogin = (token, role, email, nom_complet = "") => {
    const u = { token, role, email, nom_complet };
    setUser(u);
    localStorage.setItem("formidoc_user", JSON.stringify(u));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("formidoc_user");
  };

  const handleUpdateUser = (updates) => {
    const u = { ...user, ...updates };
    setUser(u);
    localStorage.setItem("formidoc_user", JSON.stringify(u));
  };

  const handleSessionExpiredClose = () => {
    setSessionExpired(false);
    handleLogout();
    // ProtectedRoute redirige automatiquement vers /login quand user=null
  };

  /* Redirect helper */
  const redirectToDashboard = () => {
    if (!user?.role) return <Navigate to="/login" replace />;
    if (user.role === "MEDECIN") return <Navigate to="/dashboard/medecin" replace />;
    if (user.role === "GESTIONNAIRE") return <Navigate to="/dashboard/collectivite" replace />;
    if (user.role === "SUPER_ADMIN") return <Navigate to="/dashboard/admin" replace />;
    if (user.role === "USAGER") return <Navigate to="/dashboard/usager" replace />;
    return <Navigate to="/login" replace />;
  };

  return (
    <BrowserRouter>

      {/* Modal session expirée */}
      {sessionExpired && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.iconWrap}>⏱</div>
            <div style={styles.title}>Session expirée</div>
            <p style={styles.text}>
              Votre session a expiré. Veuillez vous reconnecter pour continuer.
            </p>
            <button
              style={styles.btn}
              onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.filter = "none")}
              onClick={handleSessionExpiredClose}
            >
              Se reconnecter
            </button>
          </div>
        </div>
      )}

      <Routes>
        {/* Public */}
        <Route path="/" element={<Landing />} />

        {/* Login pages */}
        <Route
          path="/login"
          element={!user ? <Login onLogin={handleLogin} /> : redirectToDashboard()}
        />

        <Route
          path="/login_admin"
          element={!user ? <AdminLogin onLogin={handleLogin} /> : redirectToDashboard()}
        />

        <Route path="/forgot-password" element={<ForgotPassword />} />

        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Role dashboards */}
        <Route
          path="/dashboard/medecin"
          element={
            <ProtectedRoute user={user} allowedRoles={["MEDECIN"]}>
              <DashboardMedecin user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/collectivite"
          element={
            <ProtectedRoute user={user} allowedRoles={["GESTIONNAIRE"]}>
              <DashboardCollectivite user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/admin"
          element={
            <ProtectedRoute user={user} allowedRoles={["SUPER_ADMIN"]}>
              <DashboardAdmin user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/usager"
          element={
            <ProtectedRoute user={user} allowedRoles={["USAGER"]}>
              <DashboardUsager user={user} onLogout={handleLogout} onUpdateUser={handleUpdateUser} />
            </ProtectedRoute>
          }
        />

        {/* Old /dashboard -> redirect */}
        <Route path="/dashboard" element={redirectToDashboard()} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
